import { mergeAttributes, Node } from "@tiptap/core"

const SUBSCRIBE_NODE = "subscribe"

interface SubscribeTranslations {
  defaultText: (userName: string) => string
  buttonText: string
  emailPlaceholder: string
}

interface SubscribeOptions {
  backendUrl?: string
  getJWT?: () => string | null
  userName?: string
  defaultFormId?: string | null
  translations: SubscribeTranslations
  subscribed?: boolean
  subscribedLabel?: string
  subscribedTitle?: string
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    subscribe: {
      insertSubscribe: (attrs?: {
        formId?: string | null
        title?: string | null
      }) => ReturnType
    }
  }
}

export const Subscribe = Node.create<SubscribeOptions>({
  name: SUBSCRIBE_NODE,
  group: "block",
  content: "inline*",

  addOptions() {
    return {
      userName: "",
      defaultFormId: null,
      translations: {
        defaultText: (_userName: string) => "",
        buttonText: "",
        emailPlaceholder: "",
      },
      subscribed: false,
      subscribedLabel: "",
      subscribedTitle: "",
    }
  },

  addAttributes() {
    return {
      buttonText: {
        default: null,
        parseHTML: element => element.getAttribute("data-button-text"),
        renderHTML: attributes =>
          attributes.buttonText
            ? { "data-button-text": attributes.buttonText }
            : {},
      },
      uid: {
        default: null,
        parseHTML: element => element.getAttribute("data-uid"),
        renderHTML: attributes =>
          attributes.uid ? { "data-uid": attributes.uid } : {},
      },
      formId: {
        default: null,
        parseHTML: element => element.getAttribute("data-form-id"),
        renderHTML: attributes =>
          attributes.formId ? { "data-form-id": attributes.formId } : {},
      },
    }
  },

  addCommands() {
    return {
      insertSubscribe:
        attrs =>
        ({ commands }) => {
          const defaultText = this.options.translations.defaultText(
            this.options.userName || "",
          )
          const title = attrs?.title?.trim() || defaultText
          return commands.insertContent({
            type: this.name,
            attrs: {
              formId: attrs?.formId ?? this.options.defaultFormId ?? null,
            },
            content: title ? [{ type: "text", text: title }] : [],
          })
        },
    }
  },

  parseHTML() {
    return [
      {
        tag: `div[data-type="${SUBSCRIBE_NODE}"]`,
        contentElement: "[data-subscribe-title]",
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    if (this.options.subscribed) {
      const title = this.options.subscribedTitle ?? ""
      const label = this.options.subscribedLabel ?? ""

      return [
        "div",
        mergeAttributes({ "data-type": SUBSCRIBE_NODE }, HTMLAttributes),
        // TODO: no need for 2 nested divs
        [
          "div",
          { "data-subscribe-header": true },
          ["div", { "data-subscribe-title": true }, title],
        ],
        [
          "a",
          {
            "data-subscribe-button": true,
            "data-subscribe-button-subscribed": true,
          },
          label,
        ],
      ]
    }

    const buttonText =
      HTMLAttributes["data-button-text"] || this.options.translations.buttonText
    const emailPlaceholder = this.options.translations.emailPlaceholder

    return [
      "div",
      mergeAttributes({ "data-type": SUBSCRIBE_NODE }, HTMLAttributes),
      [
        "div",
        { "data-subscribe-header": true },
        ["div", { "data-subscribe-title": true }, 0],
      ],
      [
        "div",
        { "data-subscribe-form": true, contenteditable: false },
        [
          "input",
          {
            "data-subscribe-input": true,
            type: "email",
            placeholder: emailPlaceholder,
            disabled: true,
          },
        ],
        [
          "button",
          {
            "data-subscribe-button": true,
            "data-type": "subscribe-button",
            type: "button",
            disabled: true,
          },
          buttonText,
        ],
      ],
    ]
  },
})
